/**
 * フォーム回収一覧 + CSV エクスポート（Phase 24c）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.5
 *
 * 機能:
 *   - useFormSubmissions(formId) で回収データ取得
 *   - キーワード検索（email / values 横断）
 *   - キャンペーン ID フィルタ
 *   - 行展開で全フィールド表示
 *   - 削除（form.submissionCount を atomic に -1）
 *   - CSV エクスポート（papaparse、UTF-8 BOM 付き）
 *   - Excel エクスポート（xlsx、列幅自動調整）
 *   - 営業フォーム(tag='sales')は dealId / salesAgentUid / 弊社紹介 列を追加
 */
import {
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowUpNarrowWide,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  Filter,
  Mail,
  Search,
  Trash2,
} from 'lucide-react';
import Papa from 'papaparse';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteFormSubmission,
  useForm,
  useFormSubmissions,
  useUpdateFormReplyStatus,
  type FormRecord,
  type FormSubmissionRecord,
} from '@/hooks/useForms';
import { formatDateTime, toDate } from '@/lib/firestoreTime';

import { FormPageTabs } from './FormPageTabs';
import { FormScheduleDialog } from './FormScheduleDialog';
import { SubmissionReplyMailDialog } from './SubmissionReplyMailDialog';
import { SubmissionsAnalyticsTab } from './analytics/SubmissionsAnalyticsTab';
import { SubmissionsSummaryTab } from './analytics/SubmissionsSummaryTab';

import { getFormTag, resolveFormReplyStatus } from '@goenhub/shared';
import type { FieldFormElement, FormReplyStatusInput } from '@goenhub/shared';

const ALL_CAMPAIGNS = '__all__';
const NO_CAMPAIGN = '__none__';

/** 返答状態フィルタ (2026-07-23)。reply_request フィールドを持つフォームでのみ表示。 */
const ALL_REPLY = '__all__';
type ReplyFilter = typeof ALL_REPLY | 'awaiting' | 'replied';

/** 手動更新セレクトの「返答対象外」を表す値。 */
const REPLY_NONE = 'none';

function formatValue(v: string | string[] | undefined): string {
  if (v === undefined) return '';
  if (Array.isArray(v)) return v.join('; ');
  return v;
}

/**
 * CSV / Excel エクスポート共通の表データ生成。
 * 営業フォーム (tag='sales') の場合は dealId / salesAgentUid / 弊社紹介 列を追加。
 */
function buildExportTable(
  form: FormRecord,
  fieldElements: FieldFormElement[],
  rowsSrc: FormSubmissionRecord[],
): { headers: string[]; rows: (string | number)[][] } {
  const isSales = getFormTag(form) === 'sales';
  const headers = [
    'submissionId',
    'submittedAt',
    'email',
    'lineUid',
    'campaignId',
    'contactId',
    'ipAddress',
    'referrer',
    'userAgent',
    ...(isSales ? ['dealId', 'salesAgentUid', '弊社紹介'] : []),
    ...fieldElements.map((f) => f.label),
  ];
  const rows = rowsSrc.map((s) => {
    const ts = toDate(s.submittedAt);
    const row: (string | number)[] = [
      s.id,
      ts ? ts.toISOString() : '',
      s.email ?? '',
      s.lineUid ?? '',
      s.campaignId ?? '',
      s.contactId ?? '',
      s.ipAddress ?? '',
      s.referrer ?? '',
      s.userAgent ?? '',
    ];
    if (isSales) {
      row.push(s.dealId ?? '', s.salesAgentUid ?? '', s.isCompanyReferral ? 'はい' : '');
    }
    for (const f of fieldElements) {
      row.push(formatValue(s.values?.[f.id]));
    }
    return row;
  });
  return { headers, rows };
}

function exportFileStamp(): string {
  return new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
}

function safeFormName(form: FormRecord): string {
  return (form.name ?? form.title ?? 'form')
    .replace(/[\s/\\?%*:|"<>]/g, '_')
    .slice(0, 40);
}

function snippet(values: Record<string, string | string[]>, max = 60): string {
  const parts: string[] = [];
  for (const v of Object.values(values)) {
    parts.push(formatValue(v));
    if (parts.join(' / ').length > max) break;
  }
  const joined = parts.join(' / ');
  return joined.length > max ? `${joined.slice(0, max)}…` : joined;
}

export function FormSubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';

  const { data: form, isLoading: formLoading } = useForm(id);
  const { data: submissions, isLoading: subsLoading } = useFormSubmissions(id);
  const deleteMutation = useDeleteFormSubmission();
  const replyStatusMutation = useUpdateFormReplyStatus();

  const [keyword, setKeyword] = useState('');
  const [campaignFilter, setCampaignFilter] = useState<string>(ALL_CAMPAIGNS);
  const [replyFilter, setReplyFilter] = useState<ReplyFilter>(ALL_REPLY);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [subTab, setSubTab] = useState<'list' | 'summary' | 'analytics'>('list');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [replyMailTarget, setReplyMailTarget] = useState<string | null>(null);

  const fieldElements = useMemo<FieldFormElement[]>(
    () =>
      (form?.elements ?? []).filter(
        (e): e is FieldFormElement => e.kind === 'field',
      ),
    [form?.elements],
  );

  // 返答要求フィールドの有無 (絞り込み UI・ラベル表示の切替に使用)
  const hasReplyField = useMemo(
    () => fieldElements.some((f) => f.fieldType === 'reply_request'),
    [fieldElements],
  );

  // 出現するキャンペーン ID を一覧化
  const campaignIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of submissions ?? []) {
      if (s.campaignId) set.add(s.campaignId);
    }
    return Array.from(set).sort();
  }, [submissions]);

  // フィルタ適用後
  const filtered = useMemo(() => {
    const list = submissions ?? [];
    return list.filter((s) => {
      if (campaignFilter === NO_CAMPAIGN && s.campaignId) return false;
      if (
        campaignFilter !== ALL_CAMPAIGNS &&
        campaignFilter !== NO_CAMPAIGN &&
        s.campaignId !== campaignFilter
      )
        return false;
      if (
        replyFilter !== ALL_REPLY &&
        resolveFormReplyStatus(fieldElements, s) !== replyFilter
      )
        return false;
      if (!keyword.trim()) return true;
      const kw = keyword.trim().toLowerCase();
      const haystack: string[] = [s.email ?? '', s.id];
      for (const v of Object.values(s.values ?? {})) {
        haystack.push(formatValue(v));
      }
      return haystack.some((h) => h.toLowerCase().includes(kw));
    });
  }, [submissions, keyword, campaignFilter, replyFilter, fieldElements]);

  // 送信日時で昇順 / 降順に並び替え（画面表示・エクスポート共通）
  const displayed = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const ta = toDate(a.submittedAt)?.getTime() ?? 0;
      const tb = toDate(b.submittedAt)?.getTime() ?? 0;
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    });
    return arr;
  }, [filtered, sortOrder]);

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    if (!form || displayed.length === 0) {
      toast.error('エクスポート対象がありません');
      return;
    }
    const { headers, rows } = buildExportTable(form, fieldElements, displayed);
    const csv = Papa.unparse({ fields: headers, data: rows });
    // Excel での文字化け防止のため UTF-8 BOM を付与
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
    triggerDownload(
      blob,
      `form_submissions_${safeFormName(form)}_${exportFileStamp()}.csv`,
    );
    toast.success(`${displayed.length} 件をエクスポートしました`);
  };

  const handleExportXlsx = () => {
    if (!form || displayed.length === 0) {
      toast.error('エクスポート対象がありません');
      return;
    }
    const { headers, rows } = buildExportTable(form, fieldElements, displayed);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // 列幅を内容に応じて自動調整（10〜60 文字）
    ws['!cols'] = headers.map((h, ci) => {
      const maxLen = rows.reduce(
        (m, r) => Math.max(m, String(r[ci] ?? '').length),
        h.length,
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '回収一覧');
    XLSX.writeFile(
      wb,
      `form_submissions_${safeFormName(form)}_${exportFileStamp()}.xlsx`,
    );
    toast.success(`${displayed.length} 件をエクスポートしました`);
  };

  const handleDelete = async (s: FormSubmissionRecord) => {
    if (!id) return;
    if (
      !window.confirm(
        '回収データを 1 件削除します。元に戻せません。続行しますか？',
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync({ id: s.id, formId: id });
      toast.success('削除しました');
    } catch (err) {
      toast.error(
        `削除失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  /**
   * 返答フラグの手動更新 (2026-08-07)。
   * form_submissions はクライアント update 禁止のため Callable 経由で書き換える。
   */
  const handleReplyStatusChange = async (
    s: FormSubmissionRecord,
    status: FormReplyStatusInput,
  ) => {
    if (!id) return;
    try {
      await replyStatusMutation.mutateAsync({
        submissionId: s.id,
        status,
        formId: id,
      });
      toast.success(
        status === 'awaiting'
          ? '「返答待ち」に変更しました'
          : status === 'replied'
            ? '「返答済み」に変更しました'
            : '「返答対象外」に変更しました',
      );
    } catch (err) {
      toast.error(
        `更新失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const toggleExpand = (subId: string) => {
    setExpanded((s) => ({ ...s, [subId]: !s[subId] }));
  };

  if (formLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (!form) {
    return (
      <Screen title="回収一覧">
        <EmptyState
          icon={ClipboardList}
          title="フォームが見つかりません"
          description="削除済か URL が誤っている可能性があります。"
        />
      </Screen>
    );
  }

  return (
    <Screen
      title={`回収一覧 — ${form.name}`}
      description="フォームに送信された回答の確認・絞り込み・CSV / Excel 書き出し。"
      actions={
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/forms')}
          >
            <ArrowLeft className="size-4 mr-1" />
            一覧へ戻る
          </Button>
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setScheduleOpen(true)}
              disabled={displayed.length === 0}
              title="表示中の回答者へメール予約配信"
            >
              <CalendarClock className="size-3.5 mr-1" />
              メール予約配信
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleExportCsv}
            disabled={filtered.length === 0}
          >
            <Download className="size-3.5 mr-1" />
            CSV
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleExportXlsx}
            disabled={filtered.length === 0}
          >
            <Download className="size-3.5 mr-1" />
            Excel
          </Button>
        </>
      }
    >
      {id && (
        <FormPageTabs
          formId={id}
          submissionCount={submissions?.length ?? form.submissionCount ?? 0}
          active="submissions"
        />
      )}

      <Tabs
        value={subTab}
        onValueChange={(v) => setSubTab(v as 'list' | 'summary' | 'analytics')}
      >
        <TabsList>
          <TabsTrigger value="list">一覧</TabsTrigger>
          <TabsTrigger value="summary">まとめ</TabsTrigger>
          <TabsTrigger value="analytics">統計</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
      {/* 検索 + 絞り込みトグル */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2">
          <Search className="size-4 text-[var(--text-muted)] flex-shrink-0" />
          <Input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="メアド・回答内容で検索"
            className="flex-1 border-0 bg-transparent !p-0 h-auto focus-visible:ring-0"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
          title="送信日時の並び順を切り替え"
        >
          {sortOrder === 'desc' ? (
            <ArrowDownWideNarrow className="size-3.5 mr-1" />
          ) : (
            <ArrowUpNarrowWide className="size-3.5 mr-1" />
          )}
          {sortOrder === 'desc' ? '新しい順' : '古い順'}
        </Button>
        {(campaignIds.length > 0 || hasReplyField) && (
          <Button
            type="button"
            variant={
              filterOpen ||
              campaignFilter !== ALL_CAMPAIGNS ||
              replyFilter !== ALL_REPLY
                ? 'primary'
                : 'ghost'
            }
            size="sm"
            onClick={() => setFilterOpen((s) => !s)}
          >
            <Filter className="size-3.5 mr-1" />
            絞り込み
            {(campaignFilter !== ALL_CAMPAIGNS || replyFilter !== ALL_REPLY) && (
              <Badge variant="accent" className="ml-1.5">
                {(campaignFilter !== ALL_CAMPAIGNS ? 1 : 0) +
                  (replyFilter !== ALL_REPLY ? 1 : 0)}
              </Badge>
            )}
          </Button>
        )}
      </div>

      {filterOpen && (campaignIds.length > 0 || hasReplyField) && (
        <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
          {campaignIds.length > 0 && (
            <>
              <Label className="text-xs !mb-0 text-[var(--text-secondary)]">
                キャンペーン
              </Label>
              <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                <SelectTrigger className="min-w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CAMPAIGNS}>すべてのキャンペーン</SelectItem>
                  <SelectItem value={NO_CAMPAIGN}>キャンペーン未指定のみ</SelectItem>
                  {campaignIds.map((cid) => (
                    <SelectItem key={cid} value={cid}>
                      {cid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          {hasReplyField && (
            <>
              <Label className="text-xs !mb-0 text-[var(--text-secondary)]">
                返答状態
              </Label>
              <Select
                value={replyFilter}
                onValueChange={(v) => setReplyFilter(v as ReplyFilter)}
              >
                <SelectTrigger className="min-w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_REPLY}>すべて</SelectItem>
                  <SelectItem value="awaiting">返答待ち</SelectItem>
                  <SelectItem value="replied">返答済み</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          {(campaignFilter !== ALL_CAMPAIGNS || replyFilter !== ALL_REPLY) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setCampaignFilter(ALL_CAMPAIGNS);
                setReplyFilter(ALL_REPLY);
              }}
            >
              クリア
            </Button>
          )}
        </div>
      )}

      <div className="text-xs text-[var(--text-muted)] mb-3">
        {filtered.length} 件
        {submissions && filtered.length !== submissions.length && (
          <> / 全 {submissions.length} 件</>
        )}
      </div>

      {/* 一覧 */}
      {subsLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : !submissions || submissions.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="まだ回収データがありません"
          description="フォームを公開し、URL を共有して回答を集めましょう。"
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Search}
          title="条件に一致する回収データがありません"
          description="検索キーワードやキャンペーンフィルタを変更してみてください。"
        />
      ) : (
        <div className="space-y-2">
          {displayed.map((s) => {
            const isOpen = !!expanded[s.id];
            const replyStatus = resolveFormReplyStatus(fieldElements, s);
            return (
              <div
                key={s.id}
                className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden"
              >
                {/* サマリ行 */}
                <button
                  type="button"
                  onClick={() => toggleExpand(s.id)}
                  className="w-full flex items-start gap-3 p-3 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <div className="mt-0.5 text-[var(--text-muted)] flex-shrink-0">
                    {isOpen ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-[var(--text-muted)]">
                        {formatDateTime(s.submittedAt)}
                      </span>
                      {s.email && (
                        <Badge variant="accent">{s.email}</Badge>
                      )}
                      {s.campaignId && (
                        <Badge variant="review">
                          campaign: {s.campaignId}
                        </Badge>
                      )}
                      {s.contactId && (
                        <Badge variant="approved">
                          contact: {s.contactId.slice(0, 8)}…
                        </Badge>
                      )}
                      {replyStatus === 'awaiting' && (
                        <Badge variant="draft">返答待ち</Badge>
                      )}
                      {replyStatus === 'replied' && (
                        <Badge variant="approved">返答済み</Badge>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] line-clamp-1">
                      {snippet(s.values ?? {}, 100) || '(回答なし)'}
                    </p>
                  </div>
                  {canEdit && (
                    <div
                      className="flex-shrink-0 flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* 返答フラグの手動更新 (2026-08-07)。
                          返答要求フィールドを持つフォーム、または既にフラグが
                          立っている回答でのみ表示する。 */}
                      {(hasReplyField || replyStatus !== null) && (
                        <Select
                          value={replyStatus ?? REPLY_NONE}
                          onValueChange={(v) =>
                            void handleReplyStatusChange(
                              s,
                              v as FormReplyStatusInput,
                            )
                          }
                        >
                          <SelectTrigger
                            className="h-8 w-[116px] text-xs"
                            title="返答フラグを手動で変更"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="awaiting">返答待ち</SelectItem>
                            <SelectItem value="replied">返答済み</SelectItem>
                            <SelectItem value={REPLY_NONE}>対象外</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyMailTarget(s.id)}
                        title="回答者へメール送信"
                      >
                        <Mail className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleDelete(s)}
                        title="削除"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  )}
                </button>

                {/* 詳細展開 */}
                {isOpen && (
                  <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
                    {fieldElements.length === 0 ? (
                      <p className="text-xs text-[var(--text-muted)]">
                        質問フィールドが定義されていません
                      </p>
                    ) : (
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                        {fieldElements.map((f) => {
                          const v = s.values?.[f.id];
                          return (
                            <div key={f.id} className="min-w-0">
                              <dt className="text-[11px] text-[var(--text-muted)] mb-0.5">
                                {f.label}
                              </dt>
                              <dd className="text-xs text-[var(--text-primary)] whitespace-pre-wrap break-words">
                                {formatValue(v) || (
                                  <span className="text-[var(--text-muted)]">
                                    —
                                  </span>
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    )}
                    <div className="pt-2 mt-2 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[var(--text-muted)] font-mono">
                      <div>submissionId: {s.id}</div>
                      {s.replySentAt && (
                        <div className="md:col-span-2 truncate">
                          返信送信: {formatDateTime(s.replySentAt)} →{' '}
                          {s.replySentTo ?? ''}
                        </div>
                      )}
                      {s.lineUid && (
                        <div className="md:col-span-2 truncate">
                          <span className="text-[#06c755] font-semibold">LINE UID:</span>{' '}
                          {s.lineUid}
                        </div>
                      )}
                      {s.ipAddress && <div>ip: {s.ipAddress}</div>}
                      {s.referrer && (
                        <div className="truncate">ref: {s.referrer}</div>
                      )}
                      {s.userAgent && (
                        <div className="md:col-span-2 truncate">
                          ua: {s.userAgent}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>

        <TabsContent value="summary">
          <SubmissionsSummaryTab form={form} submissions={submissions ?? []} />
        </TabsContent>

        <TabsContent value="analytics">
          <SubmissionsAnalyticsTab form={form} submissions={submissions ?? []} />
        </TabsContent>
      </Tabs>

      {id && scheduleOpen && (
        <FormScheduleDialog
          formId={id}
          formName={form.name}
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          submissions={displayed}
        />
      )}

      {id && (
        <SubmissionReplyMailDialog
          formId={id}
          submissionId={replyMailTarget}
          open={!!replyMailTarget}
          onOpenChange={(open) => {
            if (!open) setReplyMailTarget(null);
          }}
        />
      )}
    </Screen>
  );
}
